/*
 * SkipTracePanel.jsx — in-dossier skip-trace UI.
 *
 * Renders inside ContactSection. Three modes:
 *   1. Idle (no result yet): "Find owner contact info" button + monthly remaining
 *   2. Loading: spinner + "Running skip-trace…"
 *   3. Results: persons with phones (DNC pills, litigator banner), emails,
 *      mailing addresses, and the date traced. A stored result is shown on
 *      open with no button. "Trace again (uses 1 credit)" + confirm appears
 *      only once 30 days have passed (server enforces the same rule).
 *
 * Pre-flight checks:
 *   - On mount, calls skipTrace.status() to know ack state and remaining cap.
 *   - If not acked, clicking the trace button opens TCPAComplianceModal first;
 *     after ack, runs the trace.
 *
 * Probate handling:
 *   - Deceased persons in results are shown but de-prioritized with a
 *     "deceased" label and dimmed styling. For probate leads the deceased
 *     IS the property owner, so they appear; the living PR's contact
 *     info is what the agent actually wants.
 *
 * Compliance:
 *   - Each phone displays a DNC pill if dnc=true.
 *   - Each person with litigator=true shows a prominent red warning banner.
 */

import { useEffect, useRef, useState } from 'react';
import { skipTrace, leadInteractions, safeErrorMessage } from '../api/client.js';
import TCPAComplianceModal from './TCPAComplianceModal.jsx';

// Founder rule (2026-11-02): a parcel can be traced once a month. The
// stored numbers/emails show whenever the dossier opens; "Trace again"
// only appears once the server says the 30-day window has passed. The
// server enforces the same rule (409 refresh_cooldown).

export default function SkipTracePanel({ pin, onAfterTrace }) {
  const [status, setStatus]   = useState(null);   // {acked, monthly_used, monthly_cap, ...}
  const [result, setResult]   = useState(null);   // {source, hit, persons, ...} | null
  const [checking, setChecking] = useState(true); // cached() not resolved yet
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [showAckModal, setShowAckModal] = useState(false);
  // Synchronous in-flight guard: state updates are async, so a fast
  // double-click could start two requests before `loading` re-renders.
  const inFlightRef = useRef(false);
  const pinRef = useRef(pin);

  // On open (and on pin change): reset, then load status + any stored
  // result. A stored result is shown immediately, with its date, and
  // no trace button — the agent never has to click to see numbers
  // they already paid for.
  useEffect(() => {
    let cancelled = false;
    pinRef.current = pin;
    setResult(null);
    setError(null);
    setChecking(true);
    skipTrace.status()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch(() => { /* show idle state with no remaining count */ });
    skipTrace.cached(pin)
      .then((r) => {
        if (cancelled) return;
        if (r && r.cached) {
          // Reshape into the same form runTrace() produces, so
          // ResultsDisplay handles both identically.
          setResult({
            source:            'cache',
            hit:               r.hit,
            persons:           r.persons,
            retrieved_at:      r.retrieved_at,
            expires_at:        r.expires_at,
            next_refresh_at:   r.next_refresh_at,
            refresh_available: r.refresh_available,
          });
        }
      })
      .catch(() => { /* no cache; agent will see idle button */ })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [pin]);

  const runTrace = async ({ force_refresh = false } = {}) => {
    if (inFlightRef.current) return;      // one request at a time
    inFlightRef.current = true;
    const tracePin = pin;
    setError(null);
    setLoading(true);
    try {
      const r = await skipTrace.lookup(tracePin, { force_refresh });
      if (pinRef.current !== tracePin) return;   // dossier changed
      setResult(r);
      if (r && r.refresh_failed) {
        // Server kept the earlier paid result and returned it.
        const msg = r.error?.message || 'The new trace failed.';
        setError(`Trace again failed: ${msg} Your earlier result is still shown.`);
      }
      // Refresh status so monthly remaining updates after a fresh trace.
      skipTrace.status().then(setStatus).catch(() => {});
      // Tell the parent dossier so it can refresh the history line.
      onAfterTrace && onAfterTrace();
    } catch (e) {
      if (pinRef.current !== tracePin) return;
      const detail = e?.detail?.detail ?? e?.detail;
      if (detail?.code === 'compliance_not_acked') {
        // Server says we need ack — open modal and stop here.
        setShowAckModal(true);
        return;
      }
      if (e?.status === 409 || detail?.code === 'refresh_cooldown'
          || detail?.code === 'trace_in_progress') {
        // Server refused to bill again. Show its reason, never "try again".
        let msg = detail?.message || 'A skip-trace for this property is not available right now.';
        if (detail?.next_refresh_at) {
          msg += ` Next trace available ${formatDate(detail.next_refresh_at)}.`;
        }
        setError(msg);
      } else if (detail?.code === 'monthly_cap_reached') {
        setError(
          `You've used your ${detail.monthly_cap} skip-traces for this `
          + `month. The cap resets at the start of next month.`
        );
      } else if (detail?.code === 'provider_error') {
        setError(detail.message);
      } else {
        setError(safeErrorMessage(e, 'Skip-trace failed'));
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleTraceClick = () => {
    if (inFlightRef.current) return;
    // Quick local check: if status says not acked, open modal first.
    // (The server also enforces this — local check is for fewer round trips.)
    if (status && !status.acked) {
      setShowAckModal(true);
      return;
    }
    runTrace();
  };

  const handleAcked = () => {
    setShowAckModal(false);
    // Refresh status so the UI reflects the ack, then run the trace
    // the agent was about to run.
    skipTrace.status().then((s) => {
      setStatus(s);
      runTrace();
    }).catch(() => {
      // Status refresh failed but ack succeeded — run anyway.
      runTrace();
    });
  };

  // Display text for the monthly counter. Operators share the agent
  // cap unless the server raises it, so everyone sees a count.
  let monthlyText = null;
  if (status && status.monthly_cap != null) {
    monthlyText = `${status.monthly_remaining} of ${status.monthly_cap} skip-traces left this month`
      + (status.is_operator ? ' (operator)' : '');
  }

  // Disable the button when the cap is reached.
  const capReached = status
    && status.monthly_cap != null
    && status.monthly_remaining === 0;

  return (
    <div style={containerStyle}>
      {!result && checking && (
        <div style={monthlyTextStyle}>Checking for an existing trace…</div>
      )}

      {!result && !checking && (
        <>
          <button
            type="button"
            onClick={handleTraceClick}
            disabled={loading || capReached}
            aria-busy={loading}
            style={traceButtonStyle(loading)}
          >
            {loading ? 'Running skip-trace…' : 'Find owner contact info'}
          </button>
          {monthlyText && (
            <div style={monthlyTextStyle}>{monthlyText}</div>
          )}
        </>
      )}

      {error && (
        <div style={errorStyle}>{error}</div>
      )}

      {result && (
        <ResultsDisplay
          result={result}
          loading={loading}
          capReached={capReached}
          onTraceAgain={() => runTrace({ force_refresh: true })}
        />
      )}

      {showAckModal && (
        <TCPAComplianceModal
          onAcked={handleAcked}
          onCancel={() => setShowAckModal(false)}
        />
      )}
    </div>
  );
}


// True once the server says the 30-day window has passed.
function canTraceAgain(result) {
  if (!result) return false;
  if (result.refresh_available) return true;
  if (result.next_refresh_at) {
    return new Date(result.next_refresh_at).getTime() <= Date.now();
  }
  return false;
}


// Date line + (after 30 days only) a deliberate, confirmed
// "Trace again (uses 1 credit)" control. Disabled while in flight.
function TraceMeta({ result, loading, capReached, onTraceAgain }) {
  const [confirming, setConfirming] = useState(false);
  const allowed = canTraceAgain(result);
  const traced = result.retrieved_at ? formatDate(result.retrieved_at) : null;

  return (
    <div style={metaRowStyle}>
      <span>
        {traced ? `Traced ${traced}` : 'Traced'}
        {!allowed && result.next_refresh_at
          && ` · can trace again ${formatDate(result.next_refresh_at)}`}
      </span>
      {allowed && !confirming && (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={loading || capReached}
          style={retryButtonStyle(loading || capReached)}
        >
          {loading ? 'Tracing…' : 'Trace again (uses 1 credit)'}
        </button>
      )}
      {allowed && confirming && (
        <span style={confirmRowStyle}>
          <span>Use 1 credit?</span>
          <button
            type="button"
            disabled={loading}
            onClick={() => { setConfirming(false); onTraceAgain(); }}
            style={retryButtonStyle(loading)}
          >
            Confirm
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => setConfirming(false)}
            style={retryButtonStyle(loading)}
          >
            Cancel
          </button>
        </span>
      )}
    </div>
  );
}


function ResultsDisplay({ result, loading, capReached, onTraceAgain }) {
  const { hit, persons } = result;
  const meta = (
    <TraceMeta
      result={result}
      loading={loading}
      capReached={capReached}
      onTraceAgain={onTraceAgain}
    />
  );

  // Detect household-fallback: server marks each person from the
  // owner-search fallback (after a probate person-search miss) with
  // _household_fallback=true. The whole result is fallback-mode iff
  // any person carries the flag.
  const isHouseholdFallback = Array.isArray(persons)
    && persons.some((p) => p && p._household_fallback);

  // Detect web-research: server marks each person found via the
  // obit → public-records → Tracerfy-verification chain with
  // _web_research=true. This is the big win for probate leads — the
  // PR's actual home address found and verified.
  const isWebResearch = Array.isArray(persons)
    && persons.some((p) => p && p._web_research);

  if (!hit || !persons.length) {
    // Build a helpful miss message. If we searched for a specific
    // named PR (search_mode='person'), the miss almost certainly
    // means the PR doesn't live at the property — common for
    // probate cases where the PR is an adult child living elsewhere.
    const searchedFor = result.searched_for;
    const isPersonSearch = result.search_mode === 'person' && searchedFor;
    return (
      <div style={missStyle}>
        <div style={missTitleStyle}>
          {isPersonSearch
            ? `${searchedFor} not found at this address`
            : 'No contact data found'}
        </div>
        <div style={missBodyStyle}>
          {isPersonSearch ? (
            <>
              The personal representative doesn&rsquo;t live at the
              property, and no household members or obituary references
              were found in public records. If the death is very recent
              (under two weeks), re-check this lead in a few weeks —
              an online obituary may surface and unlock the PR&rsquo;s
              address. Otherwise, the deceased&rsquo;s family may not
              have an online footprint; a handwritten letter to the
              property address remains a reasonable path.
            </>
          ) : (
            <>
              Skip-trace returned no matches for this address. This
              can happen for vacant properties, recently transferred
              ownership, or addresses where the resident&rsquo;s
              identity isn&rsquo;t public. Try again in 30 days, or
              contact this lead by mail to the property address.
            </>
          )}
        </div>
        {meta}
      </div>
    );
  }

  // Litigator warning: any person flagged → big red banner.
  const anyLitigator = persons.some((p) => p.litigator);

  // Sort: living non-deceased property owners first, then living
  // non-owners, then deceased last (probate use case — the deceased
  // homeowner is expected to appear).
  const sorted = [...persons].sort((a, b) => {
    if (a.deceased && !b.deceased) return 1;
    if (!a.deceased && b.deceased) return -1;
    if (a.property_owner && !b.property_owner) return -1;
    if (!a.property_owner && b.property_owner) return 1;
    return 0;
  });

  return (
    <div>
      {isWebResearch && (
        <div style={webResearchBannerStyle}>
          <div style={fallbackBannerTitleStyle}>
            {result.searched_for
              ? `${result.searched_for} — found via public records`
              : 'Personal representative — found via public records'}
          </div>
          <div style={fallbackBannerBodyStyle}>
            The PR doesn&rsquo;t live at the property. Their home
            address was identified through the deceased&rsquo;s
            obituary and verified by skip-trace. Contact info below is
            for the actual decision-maker — likely your highest-value
            path on this lead.
          </div>
        </div>
      )}

      {isHouseholdFallback && !isWebResearch && (
        <div style={fallbackBannerStyle}>
          <div style={fallbackBannerTitleStyle}>
            {result.searched_for
              ? `${result.searched_for} not found at this address`
              : 'Personal representative not found at this address'}
          </div>
          <div style={fallbackBannerBodyStyle}>
            The household contact{persons.length > 1 ? 's' : ''} below
            may be at the home and could forward your letter to the
            PR. Common with surviving spouses or other family members
            at the property. Send a handwritten letter to the property
            address — it often reaches the decision-maker.
          </div>
        </div>
      )}

      {anyLitigator && (
        <div style={litigatorBannerStyle}>
          <strong>TCPA LITIGATOR FLAGGED.</strong> One or more numbers
          below are linked to a known TCPA litigator. Do not call or
          text these numbers.
        </div>
      )}

      {sorted.map((p, i) => (
        <PersonCard
          key={`${p.full_name}-${i}`}
          person={p}
          isHouseholdFallback={!!p._household_fallback}
          isWebResearch={!!p._web_research}
        />
      ))}

      {meta}
    </div>
  );
}



function PersonCard({ person, isHouseholdFallback, isWebResearch }) {
  const dim = person.deceased;
  return (
    <div style={personCardStyle(dim)}>
      <div style={personHeaderStyle}>
        <span style={personNameStyle}>
          {person.full_name || `${person.first_name || ''} ${person.last_name || ''}`.trim()}
        </span>
        <span style={personFlagsStyle}>
          {isWebResearch ? (
            <Pill color="var(--accent)" bg="var(--accent-dim)">
              Personal Representative
            </Pill>
          ) : isHouseholdFallback ? (
            <Pill color="var(--text-secondary)" bg="var(--bg-input)">
              Household
            </Pill>
          ) : (
            person.property_owner && (
              <Pill color="var(--accent)" bg="var(--accent-dim)">Property owner</Pill>
            )
          )}
          {person.deceased && (
            <Pill color="var(--text-tertiary)" bg="var(--bg-input)">Deceased</Pill>
          )}
          {person.litigator && (
            <Pill color="var(--call-now)" bg="var(--call-now-bg)">Litigator</Pill>
          )}
          {person.age && !person.deceased && (
            <span style={personAgeStyle}>age {person.age}</span>
          )}
        </span>
      </div>

      {person.mailing_address && (person.mailing_address.street || person.mailing_address.city) && (
        <div style={mailingAddressStyle}>
          {[
            person.mailing_address.street,
            [person.mailing_address.city, person.mailing_address.state, person.mailing_address.zip]
              .filter(Boolean).join(', '),
          ].filter(Boolean).join(' · ')}
        </div>
      )}

      {(person.phones || []).length > 0 && (
        <div style={fieldGroupStyle}>
          <div style={fieldLabelStyle}>Phones</div>
          {person.phones.map((ph, i) => (
            <div key={i} style={phoneRowStyle}>
              <a href={`tel:${ph.number}`} style={phoneNumberStyle}>
                {formatPhone(ph.number)}
              </a>
              <span style={phoneMetaStyle}>
                {ph.type}{ph.carrier ? ` · ${ph.carrier}` : ''}
              </span>
              {ph.dnc && (
                <Pill color="var(--call-now)" bg="var(--call-now-bg)" tight>DNC</Pill>
              )}
            </div>
          ))}
        </div>
      )}

      {(person.emails || []).length > 0 && (
        <div style={fieldGroupStyle}>
          <div style={fieldLabelStyle}>Emails</div>
          {person.emails.map((em, i) => (
            <div key={i} style={emailRowStyle}>
              <a href={`mailto:${em.email}`} style={emailLinkStyle}>
                {em.email}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Pill({ children, color, bg, tight }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: tight ? '1px 6px' : '2px 8px',
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color,
      background: bg,
      borderRadius: 10,
      fontFamily: 'var(--font-sans)',
    }}>
      {children}
    </span>
  );
}


function formatPhone(num) {
  if (!num) return '';
  const digits = num.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  }
  return num;
}


function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}


// ── Styles ────────────────────────────────────────────────────────

const containerStyle = {
  marginTop: 12,
};

const traceButtonStyle = (loading) => ({
  width: '100%',
  padding: '10px 14px',
  fontSize: 13,
  fontWeight: 600,
  background: 'var(--bg-input)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-md, 6px)',
  cursor: loading ? 'wait' : 'pointer',
  fontFamily: 'var(--font-sans)',
  letterSpacing: '0.01em',
  opacity: loading ? 0.6 : 1,
});

const monthlyTextStyle = {
  marginTop: 4,
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontFamily: 'var(--font-sans)',
  textAlign: 'center',
};

const errorStyle = {
  marginTop: 8,
  padding: '8px 10px',
  background: 'var(--call-now-bg, #fff0f0)',
  border: '1px solid var(--call-now)',
  color: 'var(--call-now)',
  fontSize: 12,
  borderRadius: 'var(--radius-md, 6px)',
  fontFamily: 'var(--font-sans)',
};

const litigatorBannerStyle = {
  padding: '10px 12px',
  background: 'var(--call-now)',
  color: '#fff',
  fontSize: 12,
  fontFamily: 'var(--font-sans)',
  borderRadius: 'var(--radius-md, 6px)',
  marginBottom: 10,
  lineHeight: 1.5,
};

// Household-fallback banner. Renders above the persons list when the
// PR-name search missed but owner-search found household members.
// Tone: explanatory, not alarming. Same warm-neutral palette as the
// rest of the dossier so it reads as context, not warning.
const fallbackBannerStyle = {
  padding: '10px 12px',
  background: 'var(--bg-input)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md, 6px)',
  marginBottom: 10,
};

// Web-research banner. Renders above the persons list when the PR
// was found via the obit→public-records→Tracerfy-verification chain.
// Gold accent border signals "this is the win case" vs the neutral
// household-fallback banner. The PR's actual contact info is the
// highest-value outcome on a probate lead.
const webResearchBannerStyle = {
  padding: '10px 12px',
  background: 'var(--accent-dim)',
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius-md, 6px)',
  marginBottom: 10,
};

const fallbackBannerTitleStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--text)',
  letterSpacing: '0.02em',
  marginBottom: 4,
};

const fallbackBannerBodyStyle = {
  fontFamily: 'var(--font-serif)',
  fontSize: 12,
  lineHeight: 1.55,
  color: 'var(--text-secondary)',
};

const personCardStyle = (dim) => ({
  padding: '10px 12px',
  marginBottom: 8,
  background: 'var(--bg-card-hover, var(--bg-input))',
  border: '0.5px solid var(--border)',
  borderRadius: 'var(--radius-md, 6px)',
  opacity: dim ? 0.55 : 1,
});

const personHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
  flexWrap: 'wrap',
  marginBottom: 6,
};

const personNameStyle = {
  fontFamily: 'var(--font-serif)',
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
};

const personFlagsStyle = {
  display: 'inline-flex',
  gap: 4,
  flexWrap: 'wrap',
  alignItems: 'center',
};

const personAgeStyle = {
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontFamily: 'var(--font-sans)',
};

const mailingAddressStyle = {
  fontSize: 12,
  color: 'var(--text-secondary)',
  fontFamily: 'var(--font-serif)',
  marginBottom: 8,
  paddingBottom: 6,
  borderBottom: '0.5px dashed var(--border)',
};

const fieldGroupStyle = {
  marginTop: 6,
};

const fieldLabelStyle = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  marginBottom: 3,
  fontFamily: 'var(--font-sans)',
};

const phoneRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 2,
  flexWrap: 'wrap',
};

const phoneNumberStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--accent)',
  textDecoration: 'none',
};

const phoneMetaStyle = {
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontFamily: 'var(--font-sans)',
};

const emailRowStyle = {
  marginBottom: 2,
};

const emailLinkStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  color: 'var(--accent)',
  textDecoration: 'none',
};

const metaRowStyle = {
  marginTop: 4,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontFamily: 'var(--font-sans)',
};

const retryButtonStyle = (disabled) => ({
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  fontSize: 10,
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  textDecoration: 'underline',
  fontFamily: 'var(--font-sans)',
  padding: 0,
});

const confirmRowStyle = {
  display: 'inline-flex',
  gap: 8,
  alignItems: 'center',
};

const missStyle = {
  padding: 12,
  background: 'var(--bg-input)',
  border: '0.5px dashed var(--border)',
  borderRadius: 'var(--radius-md, 6px)',
};

const missTitleStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--text-secondary)',
  letterSpacing: '0.04em',
  marginBottom: 4,
};

const missBodyStyle = {
  fontFamily: 'var(--font-serif)',
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--text-secondary)',
};
